import Box from "@/common/components/Box"
import Button from "@/common/components/Button"
import { ChangeHistory } from "@/common/components/ChangeHistory"
import { RequestedReview } from "@/features/establishmentLeadsFunnel/components/RequestedReview"
import { AddressSection } from "@/features/privateCompanyLeadsFunnel/components/AddressSection"
import { Checklist } from "@/features/privateCompanyLeadsFunnel/components/Checklist"
import { ContactsSection } from "@/features/privateCompanyLeadsFunnel/components/ContactsSection"
import { GeneralInformationSection } from "@/features/privateCompanyLeadsFunnel/components/GeneralInformationSection"
import { GenericDocumentsSection } from "@/features/privateCompanyLeadsFunnel/components/GenericDocumentsSection"
import { LeadHeader } from "@/features/privateCompanyLeadsFunnel/components/LeadHeader"
import { OperationalInformationSection } from "@/features/privateCompanyLeadsFunnel/components/OperationalInformationSection"
import { ResponsibleSection } from "@/features/privateCompanyLeadsFunnel/components/ResponsibleSection"
import { stateStrategy, translateStateStrategy } from "@/features/privateCompanyLeadsFunnel/constants/stateStrategy.constant"
import useAdvancePrivateCompanyLead from "@/features/privateCompanyLeadsFunnel/hooks/useAdvancePrivateCompanyLead"
import usePrivateCompanyLeadForm from "@/features/privateCompanyLeadsFunnel/hooks/usePrivateCompanyLeadForm"
import useRegressLead from "@/features/privateCompanyLeadsFunnel/hooks/useRegressLead"
import useReviewSectionLead from "@/features/privateCompanyLeadsFunnel/hooks/useReviewSectionLead"
import { LeadFormStateProps } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead"
import { useMemo } from "react"
import { FormProvider } from "react-hook-form"

const LeadQualifyingManagementReviewForm = ({ id, privateCompanyLead }: LeadFormStateProps) => {
  const {
    serverError,
    formMethods,
    updateLeadOnSuccess,
    redirectOnSuccess,
    onError,
    passedEveryRequirement,
    submit,
    submitManagers,
    updatePrivateCompanyLeadManagersIsPending,
  } = usePrivateCompanyLeadForm({ privateCompanyLead });

  const {
    control,
    register,
    formState: { errors },
  } = formMethods

  const hasDisapprovedReview = useMemo(
    () => {
      return privateCompanyLead?.last_review?.state === 'disapproved' ||
        privateCompanyLead.operational_information?.last_review?.state === 'disapproved' ||
        privateCompanyLead.general_information?.last_review?.state === 'disapproved' ||
        privateCompanyLead.address?.last_review?.state === 'disapproved' ||
        privateCompanyLead?.contacts?.some(
          (contact) => contact.last_review?.state === 'disapproved' || contact.documents?.some(
            (doc) => doc.last_review?.state === 'disapproved'
          )
        )
    },
    [privateCompanyLead]
  );

  const {
    mutate: advancePrivateCompanyLead,
    isPending: advancePrivateCompanyLeadIsPending
  } = useAdvancePrivateCompanyLead({
    id,
    onSuccess: () => redirectOnSuccess('/private-clients'),
    onError
  })

  const {
    mutate: regressLead
  } = useRegressLead({
    id,
    onSuccess: updateLeadOnSuccess,
    onError
  })

  const {
    mutate: approveOperationalInformationLead
  } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'approved',
    section: 'operational_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError
  })

  const {
    mutate: declineOperationalInformationLead
  } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'disapproved',
    section: 'operational_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError
  })

  const {
    mutate: approveGeneralInformationLead
  } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'approved',
    section: 'general_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError
  })

  const {
    mutate: declineGeneralInformationLead
  } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'disapproved',
    section: 'general_information_attributes',
    onSuccess: updateLeadOnSuccess,
    onError
  })

  const {
    mutate: approveAddressLead
  } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'approved',
    section: 'address_attributes',
    payload: privateCompanyLead.address,
    onSuccess: updateLeadOnSuccess,
    onError
  })

  const {
    mutate: declineAddressLead
  } = useReviewSectionLead({
    privateCompanyLeadId: id,
    state: 'disapproved',
    section: 'address_attributes',
    payload: privateCompanyLead.address,
    onSuccess: updateLeadOnSuccess,
    onError
  })

  return (
    <FormProvider {...formMethods}>
      <form>
        <LeadHeader
          privateCompanyLeadId={id}
          display_name={privateCompanyLead.display_name}
          url={privateCompanyLead.general_information?.company_logo?.url}
          showArchiveButton
          archived_at={privateCompanyLead.archived_at}
          buttons={
            <div className="flex gap-4">
              <Button type="button" disabled={!hasDisapprovedReview} variant="yellow" onClick={() => regressLead()}>Solicitar revisão</Button>
              <Button
                type="button"
                className={passedEveryRequirement ? "shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" : ''}
                variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                disabled={!passedEveryRequirement || advancePrivateCompanyLeadIsPending}
                onClick={() => advancePrivateCompanyLead()}
                isLoading={advancePrivateCompanyLeadIsPending}
                name="advance_lead"
              >
                Enviar para análise financeira
              </Button>
            </div>
          }
        />
        <div className='flex gap-6'>
          <Box className="w-full flex flex-col gap-6 h-[calc(100vh-130px)] overflow-y-scroll">
            {serverError && <p className="text-sm text-danger-soft">Ocorreu um erro no servidor, tente novamente</p>}
            {privateCompanyLead.last_review?.state === 'disapproved' && (
              <RequestedReview description={privateCompanyLead.last_review?.description} />
            )}
            <ResponsibleSection
              privateCompanyLead={privateCompanyLead}
              submit={submitManagers}
              submitObservations={submit}
              disabled={advancePrivateCompanyLeadIsPending || updatePrivateCompanyLeadManagersIsPending}
            />
            <OperationalInformationSection
              operational_information={privateCompanyLead.operational_information}
              control={control}
              register={register}
              errors={errors}
              approveOperationalInformationLead={approveOperationalInformationLead}
              declineOperationalInformationLead={declineOperationalInformationLead}
              disabled
            />
            <GeneralInformationSection
              general_information={privateCompanyLead.general_information}
              register={register}
              errors={errors}
              approveGeneralInformationLead={approveGeneralInformationLead}
              declineGeneralInformationLead={declineGeneralInformationLead}
              disabled
            />
            <AddressSection
              address={privateCompanyLead.address}
              errors={errors}
              approveAddressLead={approveAddressLead}
              declineAddressLead={declineAddressLead}
              disabled
            />
            <ContactsSection
              privateCompanyLeadId={id}
              leadState={privateCompanyLead?.state}
              contacts={privateCompanyLead?.contacts}
              contact_for_master_user={privateCompanyLead?.contact_for_master_user}
              control={control}
            />
            <GenericDocumentsSection
              privateCompanyLeadId={id}
              generic_documents={privateCompanyLead.generic_documents}
              control={control}
              disabled={advancePrivateCompanyLeadIsPending}
            />
          </Box>
          <div className="flex flex-col gap-4">
            <Checklist
              requirement_list_summary={privateCompanyLead.requirement_list_summary}
              state={translateStateStrategy[stateStrategy[privateCompanyLead.state as keyof typeof stateStrategy]]}
              className="flex flex-col gap-2 justify-between h-[calc((100vh-276px)/2)]"
              Buttons={() => (
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    disabled={!hasDisapprovedReview}
                    variant={!hasDisapprovedReview ? 'yellow' : 'yellow-black'}
                    onClick={() => regressLead()}
                  >
                    Solicitar revisão
                  </Button>
                  <Button
                    type="button"
                    variant={passedEveryRequirement ? 'default' : 'outline-disabled'}
                    disabled={!passedEveryRequirement || advancePrivateCompanyLeadIsPending}
                    onClick={() => advancePrivateCompanyLead()}
                    isLoading={advancePrivateCompanyLeadIsPending}
                  >
                    Enviar para análise financeira
                  </Button>
                </div>
              )}
            />
            <ChangeHistory logs={privateCompanyLead?.logs || []} entityId={id} />
          </div>
        </div>
      </form>
    </FormProvider>
  )
}

export default LeadQualifyingManagementReviewForm
